import React from 'react';
import {useLocation} from 'react-router-dom';
import {styled, Box} from '@mui/material';
import {
  Icon,
  designSystemToken,
  Tooltip,
  Body,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import NavLink from '@/components/nav-link';
import {media} from '@/styles/mediaQueries';
import {MenuItem, MenuItemIcon} from '../menuItems';
import {CUBIC_BEZIER, IconWrapper as BaseIconWrapper} from './styles';

export interface SideNavItemProps {
  item: MenuItem;
  isUnlocked: boolean;
  showUnreadIndicator: boolean;
  open: boolean;
  isMobile: boolean;
}

const TOOLTIP_SLOT_PROPS = {
  tooltip: {sx: {fontSize: '14px'}},
};

const LOCKED_STYLE = {opacity: 0.5, cursor: 'not-allowed'};
const LINK_STYLE = {textDecoration: 'none' as const, width: '100%'};

const ItemContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !['active'].includes(prop),
})<{active?: boolean}>(({active}) => ({
  'height': '32px',
  'boxSizing': 'border-box',
  'display': 'flex',
  'alignItems': 'center',
  'gap': '8px',
  'width': '100%',
  'padding': '0 8px',
  'borderRadius': '8px',
  'background': active
    ? designSystemToken('semantic.bg.neutral-inverse')
    : 'transparent',
  'cursor': 'pointer',
  'transition': 'background 0.2s',
  'textDecoration': 'none',
  'flexShrink': 0,
  '&:hover': {
    background: designSystemToken('semantic.bg.tertiary'),
  },

  [media.mobile]: {
    width: '60px',
    height: '60px',
    minWidth: '60px',
    justifyContent: 'center',
    padding: '4px',
    flexDirection: 'column',
  },
}));

const IconWrapper = styled(BaseIconWrapper)({
  position: 'relative',

  [media.mobile]: {
    transform: 'scale(1.2)',
  },
});

const UnreadIndicator = styled(Box)(() => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: designSystemToken('reference.blue.40'),
  position: 'absolute',
  top: '0px',
  left: '0px',
}));

const StyledBody = styled(Body, {
  shouldForwardProp: (prop: string) => !['open'].includes(prop),
})<{open: boolean}>(({open}) => ({
  maxWidth: open ? 180 : 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  display: 'inline-block',
  opacity: open ? 1 : 0,
  transition: `max-width 0.3s ${CUBIC_BEZIER}, opacity 0.2s`,
}));

type IconAppearance = React.ComponentProps<typeof Icon>['appearance'];

function MenuIcon({
  icon,
  label,
  appearance = 'neutral',
}: {
  icon: MenuItemIcon;
  label: string;
  appearance?: IconAppearance;
}) {
  if (icon.type === 'image') {
    const {type: _, ...imgProps} = icon;
    return <img alt={label} {...imgProps} />;
  }
  const {type: _, name, ...rest} = icon;
  return <Icon name={name} size="medium" appearance={appearance} {...rest} />;
}

const StartContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
  overflow: 'hidden',

  [media.mobile]: {
    overflow: 'visible',
  },
});

function ItemContent({
  item,
  isActive,
  isUnlocked,
  showUnreadIndicator,
  open,
  isMobile,
  label,
  testID,
}: SideNavItemProps & {
  isActive: boolean;
  label: string;
  testID: string;
}) {
  return (
    <Tooltip
      title={label}
      arrow
      type="light"
      placement="right"
      disableHoverListener={open}
      slotProps={TOOLTIP_SLOT_PROPS}
    >
      <ItemContainer
        data-testid={testID}
        active={isActive}
        sx={!isUnlocked ? LOCKED_STYLE : undefined}
      >
        <StartContent>
          <IconWrapper>
            {showUnreadIndicator && (
              <UnreadIndicator data-testid={`${testID}--indicator`} />
            )}
            <MenuIcon
              icon={item.startIcon}
              label={label}
              appearance={isActive ? 'primary' : 'neutral'}
            />
          </IconWrapper>
          {!isMobile && (
            <StyledBody
              open={open}
              size="lg"
              color={designSystemToken('semantic.fg.primary')}
            >
              {label}
            </StyledBody>
          )}
        </StartContent>
        {!isMobile && open && item.endIcon && (
          <MenuIcon icon={item.endIcon} label={label} />
        )}
      </ItemContainer>
    </Tooltip>
  );
}

function SideNavItem({
  item,
  isUnlocked,
  showUnreadIndicator,
  open,
  isMobile,
}: SideNavItemProps) {
  const {pathname} = useLocation();
  const label = translate(item.localeLabelKey);
  const testID = `menu-item--${item.id}`;
  const matchesActiveMatch = Boolean(
    item.activeMatch && pathname.startsWith(item.activeMatch)
  );

  const contentProps = {
    item,
    isUnlocked,
    showUnreadIndicator,
    open,
    isMobile,
    label,
    testID,
  };

  if (!isUnlocked) {
    return (
      <div style={LINK_STYLE}>
        <ItemContent {...contentProps} isActive={false} />
      </div>
    );
  }

  return (
    <NavLink
      to={item.link}
      end={item.useEndMatch !== false}
      target={item.target}
      style={LINK_STYLE}
    >
      {({isActive}) => (
        <ItemContent
          {...contentProps}
          isActive={isActive || matchesActiveMatch}
        />
      )}
    </NavLink>
  );
}

export default SideNavItem;
